import { createBrowserClient } from "@supabase/ssr";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/types/database";
import { getActiveAgencySessionId } from "@/features/agency/session-store";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_PUBLISHABLE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
  throw new Error(
    "Missing required Supabase env vars: VITE_SUPABASE_URL and VITE_SUPABASE_PUBLISHABLE_KEY"
  );
}

/**
 * Browser-side Supabase client for the Relay app.
 *
 * Relay shares the Supabase project + auth.users + public.organizations
 * with Forge. Relay product tables live in the `relay.*` schema; Forge
 * tables live in `public.*`. By setting `db.schema = "relay"` here, an
 * unqualified `.from("contacts")` call resolves to `relay.contacts`. Use
 * `.schema("public").from("organizations")` (and similar) when reaching
 * across the schema boundary — this makes cross-product reads explicit
 * at the call site rather than implicit and surprising.
 *
 * The `<Database>` generic enables FK-aware return-type narrowing for
 * embedded joins (e.g. `select("*, contact:contacts(...)")` types as
 * `contact: T | null` when the FK is many-to-one, instead of `T[]`).
 * Without it, every embedded join requires an `as unknown as` workaround.
 */
// Second generic ("relay") narrows the DEFAULT schema for type inference.
// supabase-js does NOT infer the default-schema name from the runtime
// db.schema config — without this explicit second generic, .from("contacts")
// would type-check against public.* tables (where it doesn't exist) instead
// of relay.contacts (where it does). The runtime + the type system stay in
// agreement only when both args agree.
export const supabase = createBrowserClient<Database, "relay">(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    db: {
      schema: "relay",
    },
    global: {
      fetch: (input, init) => {
        const sessionId = getActiveAgencySessionId();

        if (!sessionId) {
          return fetch(input, init);
        }

        const headers = new Headers(init?.headers);
        headers.set("x-support-session-id", sessionId);

        return fetch(input, {
          ...init,
          headers,
        });
      },
    },
  },
);

/**
 * Anonymous (session-less) Supabase client for PUBLIC booker-page reads.
 *
 * The booker confirmation page (`/booking/confirmed/:id`) must read its booking
 * regardless of whether the visitor happens to have a Relay session in the same
 * browser. The shared `supabase` client above (createBrowserClient) attaches the
 * signed-in JWT, so PostgREST would evaluate such a request as `authenticated` —
 * but the public confirmation policy `public_booking_confirmation_read` is scoped
 * to `anon` (so it cannot leak other tenants' bookings to authenticated users via
 * a status-only predicate; see migration 20260616160000). This client never
 * attaches a session, so public reads are always evaluated as `anon` and the
 * policy applies for signed-in and signed-out visitors alike. Use it ONLY for
 * intentionally public, unauthenticated reads.
 */
export const supabasePublic = createClient<Database, "relay">(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    db: {
      schema: "relay",
    },
    auth: {
      persistSession: false,
      autoRefreshToken: false,
    },
  },
);
